import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { HeartPulse, User, Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "", role: "patient" });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accepted) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, terms_accepted: true, privacy_accepted: true });
      toast.success("Account created");
      navigate(u.role === "patient" ? "/assistant" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 text-[#0D5C75] font-heading font-extrabold text-lg">
            <HeartPulse /> SarvaSeva
          </Link>
          <h1 className="font-heading text-3xl font-bold text-[#101828]">Create your account</h1>
          <p className="text-muted-foreground mt-1">Start planning safe, accredited treatment abroad.</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { v: "patient", label: "I'm a Patient", icon: User },
              { v: "provider", label: "I'm a Provider", icon: Stethoscope },
            ].map((r) => (
              <button key={r.v} type="button" onClick={() => setForm({ ...form, role: r.v })}
                className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                  form.role === r.v ? "border-[#0D5C75] bg-[#E9F5F9]" : "border-[#E4E7EC] bg-white hover:bg-[#F7F9FC]"
                }`} data-testid={`role-${r.v}`}>
                <r.icon size={22} className="text-[#0D5C75]" />
                <div className="font-medium text-sm mt-2 text-[#101828]">{r.label}</div>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={set("name")} className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" required data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" required data-testid="register-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={form.password} onChange={set("password")} className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" required minLength={6} data-testid="register-password-input" />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input id="country" value={form.country} onChange={set("country")} placeholder="e.g. USA" className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" data-testid="register-country-input" />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <div className="flex items-start gap-2.5">
              <Checkbox id="accept-terms" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)}
                className="mt-0.5" data-testid="register-terms-checkbox" />
              <Label htmlFor="accept-terms" className="text-sm font-normal text-[#475467] leading-relaxed cursor-pointer">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#0D5C75] underline font-medium">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="text-[#0D5C75] underline font-medium">Privacy Policy</Link>.
              </Label>
            </div>
            <Button type="submit" disabled={loading || !accepted}
              className="w-full h-11 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-[#0D5C75] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1710074213379-2a9c2653046a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Recovery suite" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2A9D8F]/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-4xl font-extrabold">Care without compromise</h2>
          <p className="text-white/90 mt-2 text-lg">Verified hospitals. Transparent pricing. Escrow-protected payments.</p>
        </div>
      </div>
    </div>
  );
}
